import { useMemo } from 'react';
import { compact, noop } from 'lodash';

import { Patient } from '@dreryk/egabinet-patient-frontend-api';
import { MedicineSimilar, MedicineSimilarQuery } from '@dreryk/egabinet-prescription-frontend-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';

import {
   LazyLoadedDataConfigurationByCqrsQuery,
   useLazyLoadedDataConfigurationByCqrsQuery,
} from '@dreryk/drerykweb-main-frontend';
import { commonTranslationKeys } from '@dreryk/react-common';
import {
   Button,
   ButtonVariant,
   createDynamicColumnsWidth,
   Padding,
   TableColumnConfiguration,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';
import { VisitDiagnoses } from '~libs.shared.visit';
import {
   MedicineActiveIngredients,
   MedicineDetailsModalOpenAction,
   MedicineName,
   MedicinePackage,
   MedicineReimbursementSelector,
   MedicineSafetyIndicators,
} from '~libs.desktop.shared.medicine';

import { medicineSelectorTranslationKeys } from '../../../../../../../translations';

const COMMERCIAL_MEDICINE_NAME_COLUMN = 'medicineFullName';

export const useMedicineSimilarDataConfiguration = (
   patientId: Patient['id'],
   withdrawnMedicineId: number
): LazyLoadedDataConfigurationByCqrsQuery<MedicineSimilar> => {
   return useLazyLoadedDataConfigurationByCqrsQuery<MedicineSimilar>(() => {
      const query = new MedicineSimilarQuery();
      query.medicineId = withdrawnMedicineId;
      query.patientId = PatientId.of(patientId);
      return query;
   });
};

type UseeMedicineSimilarTableColumnConfigurationOptions = {
   patientId: Patient['id'];
   onMedicineSelection: (medicine: MedicineSimilar) => void;
   visitDiagnoses: VisitDiagnoses;
};

// eslint-disable-next-line max-lines-per-function
export const useMedicineSimilarTableColumnConfiguration = (
   options: UseeMedicineSimilarTableColumnConfigurationOptions
): TableColumnConfiguration<MedicineSimilar>[] => {
   // eslint-disable-next-line max-lines-per-function
   return useMemo<TableColumnConfiguration<MedicineSimilar>[]>((): TableColumnConfiguration<MedicineSimilar>[] => {
      const medicineDatabaseExtendedAvailable = protectFeature('extendedMedicineDatabase');

      const columnsWidths = createDynamicColumnsWidth({
         [COMMERCIAL_MEDICINE_NAME_COLUMN]: medicineDatabaseExtendedAvailable ? '65%' : '50%',
         safetyIndicators: medicineDatabaseExtendedAvailable ? '82px' : '0px',
         package: medicineDatabaseExtendedAvailable ? '35%' : '50%',
         options: '78px',
      });

      return compact([
         {
            name: COMMERCIAL_MEDICINE_NAME_COLUMN,
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.commercialName,
            width: columnsWidths[COMMERCIAL_MEDICINE_NAME_COLUMN],

            renderer: (props) => (
               <div>
                  <MedicineDetailsModalOpenAction
                     medicine={props.bean.medicine}
                     render={(openModal) => (
                        <MedicineName
                           name={props.bean.medicine.fullName}
                           atc={props.bean.medicine.atc}
                           onClick={openModal}
                        />
                     )}
                  />
                  <MedicineActiveIngredients activeIngredients={props.bean.medicine.activeIngredients} />
                  <MedicineReimbursementSelector
                     reimbursements={props.bean.reimbursements}
                     disabled={false}
                     visitDiagnoses={options.visitDiagnoses}
                  />
               </div>
            ),
         },
         medicineDatabaseExtendedAvailable
            ? {
                 name: 'safetyIndicators',
                 accessor: noop,
                 width: columnsWidths.safetyIndicators,
                 renderer: (props) => <MedicineSafetyIndicators medicineId={props.bean.medicine.id} />,
                 sortingDisabled: true,
              }
            : undefined,
         {
            name: 'package',
            accessor: noop,
            label: medicineSelectorTranslationKeys.table.package,
            width: columnsWidths.package,
            renderer: (props) => (
               <MedicinePackage
                  form={props.bean.medicine.form}
                  dose={props.bean.medicine.dose}
                  packageContent={props.bean.medicine.packageContent}
               />
            ),
            sortingDisabled: true,
         },
         {
            name: 'options',
            accessor: noop,
            label: '',
            renderer: (props) => (
               <Button
                  testId={'chooseMedicine'}
                  label={commonTranslationKeys.choose}
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => options.onMedicineSelection(props.bean)}
                  type={'button'}
                  compact
               />
            ),
            width: columnsWidths.options,
            sortingDisabled: true,
            padding: Padding.NONE,
         },
      ]);
   }, [options]);
};
